import {
  Box,
  Button,
  Flex,
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
} from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { JURISDICTION_LIST } from '@/lib/constants';
import {
  RENEWAL_INTERVAL_OPTIONS,
  RenewalPeriod,
  TService,
} from '@/models/services';

// @todo: make description text non editable when edited in a drawer
// @todo: display id

export default function GeneralDetails({
  form,
  hideStandaloneFlag = false,
}: {
  form: UseFormReturnType<
    Omit<TService, 'id' | 'children' | 'parents' | 'updatedAt'>
  >;
  hideStandaloneFlag: boolean;
}) {
  return (
    <>
      <Text m="sm" fz={'sm'} fw={500}>
        General details
      </Text>
      <Flex px={'sm'} pb={'sm'} direction={'column'}>
        <Textarea
          size="xs"
          label="Name"
          autosize
          minRows={1}
          maxRows={4}
          placeholder="Company Formation"
          key={form.key('name')}
          {...form.getInputProps('name')}
        />
        <Select
          allowDeselect={false}
          size="xs"
          label="Type"
          data={[
            'Corporate Services',
            'License obtainment assistance',
            'Document preparation',
            'Legal Services',
            'Account opening assistance',
            'Reselling Agent Services',
            'In-House',
            'Nominee Director and Shareholder',
          ]}
          key={form.key('type')}
          {...form.getInputProps('type')}
        />
        <Select
          allowDeselect={false}
          size="xs"
          label="Industry"
          data={[
            'Crypto/Blockchain',
            'Payments/Banking',
            'Gambling/Gaming',
            'Simple Company',
          ]}
          key={form.key('industry')}
          {...form.getInputProps('industry')}
        />
        <Select
          size="xs"
          label="Jurisdiction"
          searchable
          clearable
          placeholder="Any jurisdiction"
          key={form.key('jurisdictions')}
          value={form.getValues().jurisdictions?.[0] || null}
          onChange={(value: string | null) => {
            form.setFieldValue('jurisdictions', value ? [value] : []);
          }}
          data={JURISDICTION_LIST.map(({ code, title }) => ({
            value: code,
            label: title,
          }))}
        />
        <Stack my={'xs'} gap={'xs'}>
          <Select
            size="xs"
            label="Invoice terms profile"
            description="Applies to installment invoices. Included services follow their package. Once a plan's first invoice is issued, its terms stay unchanged."
            allowDeselect={false}
            data={[
              { value: 'standard', label: 'Standard services — Clause 9.16' },
              {
                value: 'casp_license',
                label: 'CASP / MiCAR license obtainment — Clause 9.15',
              },
            ]}
            key={form.key('invoiceTermsProfile')}
            {...form.getInputProps('invoiceTermsProfile')}
          />
          {hideStandaloneFlag !== true && (
            <>
              <Switch
                size="xs"
                label="Standalone service (cannot be included in other services)"
                key={form.key('standalone')}
                {...form.getInputProps('standalone', { type: 'checkbox' })}
              />
              <Switch
                size="xs"
                label="Additional service (can be added to offers)"
                key={form.key('isAdditionalService')}
                {...form.getInputProps('isAdditionalService', {
                  type: 'checkbox',
                })}
              />
            </>
          )}
          {/* IS-121: deliberately outside the guard above - a mandatory marker
              has to be settable on child services too, and that guard is what
              hides the other two flags for them. */}
          <Switch
            size="xs"
            label="Mark as mandatory"
            description="Shows an asterisk after the service name in proposals"
            key={form.key('isMandatory')}
            {...form.getInputProps('isMandatory', { type: 'checkbox' })}
          />
        </Stack>
        <Textarea
          label="Description"
          description="Publicly visible description of the service"
          size="xs"
          key={form.key('description')}
          {...form.getInputProps('description')}
          autosize
          minRows={2}
          maxRows={10}
        />
      </Flex>
    </>
  );
}

export function RenewalSection({
  form,
}: {
  form: UseFormReturnType<
    Omit<TService, 'id' | 'children' | 'parents' | 'updatedAt'>
  >;
}) {
  const renewal = form.getValues().renewal;
  const isRenewable = renewal?.isRenewable === true;

  if (!isRenewable) {
    return (
      <>
        <Text m="sm" fz="sm" fw={500}>
          Recurrence
        </Text>
        <Box px="sm" pb="sm">
          <Text fz="xs" c="dimmed" my="xs">
            This service is not renewable. Converting to renewable is
            irreversible.
          </Text>
          <Button
            size="xs"
            variant="light"
            onClick={() => {
              form.setFieldValue('renewal', {
                ...renewal,
                isRenewable: true,
                period: RenewalPeriod.Annually,
                renewalPrice: undefined,
                offsetDays: 0,
                fineType: undefined,
                fineAmount: 0,
                incrementValue: 0,
                incrementInterval: undefined,
              });
            }}
          >
            Convert to Renewable
          </Button>
        </Box>
      </>
    );
  }

  const fineType = renewal?.fineType;

  return (
    <>
      <Text m="sm" fz="sm" fw={500}>
        Renewal Configuration
      </Text>
      <Stack gap="xs" px="sm" pb="sm">
        <Select
          size="xs"
          label="Interval"
          allowDeselect={false}
          data={RENEWAL_INTERVAL_OPTIONS}
          value={String(renewal?.period ?? RenewalPeriod.Annually)}
          onChange={(v) => {
            form.setFieldValue('renewal.period', Number(v));
          }}
        />
        <NumberInput
          size="xs"
          label="Renewal price"
          description="Price charged on renewal. Leave empty to use the service price."
          min={0}
          decimalScale={2}
          placeholder="Same as service price"
          allowNegative={false}
          key={form.key('renewal.renewalPrice')}
          {...form.getInputProps('renewal.renewalPrice')}
        />
        <NumberInput
          size="xs"
          label="Offset days"
          description="Days before renewal date the service must be renewed to avoid fines"
          min={0}
          key={form.key('renewal.offsetDays')}
          {...form.getInputProps('renewal.offsetDays')}
        />
        <Select
          size="xs"
          label="Fine type"
          allowDeselect={false}
          data={[
            { value: 'none', label: 'None' },
            { value: 'static', label: 'Static (fixed amount)' },
            { value: 'incremental', label: 'Incremental (growing over time)' },
          ]}
          value={fineType ?? 'none'}
          onChange={(v) => {
            form.setFieldValue(
              'renewal.fineType',
              v === 'none' ? undefined : (v as 'static' | 'incremental'),
            );
            if (v === 'none') {
              form.setFieldValue('renewal.fineAmount', 0);
              form.setFieldValue('renewal.incrementValue', 0);
              form.setFieldValue('renewal.incrementInterval', undefined);
            }
          }}
        />
        {fineType && (
          <NumberInput
            size="xs"
            label="Fine amount"
            min={0}
            key={form.key('renewal.fineAmount')}
            {...form.getInputProps('renewal.fineAmount')}
          />
        )}
        {fineType === 'incremental' && (
          <>
            <NumberInput
              size="xs"
              label="Increment value"
              description="Additional amount added per interval"
              min={0}
              key={form.key('renewal.incrementValue')}
              {...form.getInputProps('renewal.incrementValue')}
            />
            <Select
              size="xs"
              label="Increment interval"
              allowDeselect={false}
              data={[
                { value: '1month', label: 'Every month' },
                { value: '1week', label: 'Every week' },
              ]}
              key={form.key('renewal.incrementInterval')}
              {...form.getInputProps('renewal.incrementInterval')}
            />
          </>
        )}
      </Stack>
    </>
  );
}
